import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { useSubscription } from "../hooks/useSubscription";

function useQuery() {
  const { search } = useLocation();
  return new URLSearchParams(search);
}

export default function JoinTeam() {
  const query = useQuery();
  const navigate = useNavigate();

  const { isLoading, isPlusActive } = useSubscription();

  const [status, setStatus] = useState("idle"); // idle | joining | success | error
  const [error, setError] = useState(null);

  const token = (query.get("token") || "").trim();

  useEffect(() => {
    let mounted = true;

    async function accept() {
      if (isLoading) return;

      if (!token) {
        if (mounted) {
          setStatus("error");
          setError("Missing invite token.");
        }
        return;
      }

      // Teams is Plus-only
      if (!isPlusActive) {
        if (mounted) {
          setStatus("error");
          setError("Teams is a Beem Plus feature. Please upgrade to join a team.");
        }
        return;
      }

      try {
        if (mounted) {
          setStatus("joining");
          setError(null);
        }

        await axiosInstance.post(
          "/api/dashboard/teams/invites/accept",
          { token },
          { withCredentials: true, headers: { "Content-Type": "application/json" } }
        );

        if (!mounted) return;

        setStatus("success");

        setTimeout(() => {
          navigate("/teams");
        }, 800);
      } catch (err) {
        console.error("[JoinTeam] acceptInvite error:", err);
        if (!mounted) return;

        setStatus("error");
        setError(err?.response?.data?.error || err.message || "Failed to accept invite.");
      }
    }

    accept();

    return () => {
      mounted = false;
    };
  }, [token, isLoading, isPlusActive, navigate]);

  return (
    <div className="pt-10 text-white max-w-2xl mx-auto px-6">
      <h1 className="text-4xl mb-6">Join team</h1>

      <div className="glass p-6 rounded-xl">
        {isLoading ? (
          <div className="text-white/70">Loading…</div>
        ) : status === "joining" ? (
          <div className="text-white/70">Joining team…</div>
        ) : status === "success" ? (
          <div>
            <p className="text-white mb-3">You’ve joined the team ✅</p>
            <p className="text-white/70 text-sm">Taking you to Teams…</p>
          </div>
        ) : status === "error" ? (
          <div>
            <p className="text-red-300 mb-4">{error || "Something went wrong."}</p>
            {!isPlusActive ? (
              <Link to="/settings" className="bg-[#AF9B8A] px-4 py-2 rounded-xl inline-block">
                Upgrade
              </Link>
            ) : (
              <Link to="/teams" className="bg-[#AF9B8A] px-4 py-2 rounded-xl inline-block">
                Go to Teams
              </Link>
            )}
          </div>
        ) : (
          <div className="text-white/70">Ready…</div>
        )}
      </div>
    </div>
  );
}
